'use client';

import { c as _c } from "react/compiler-runtime";
import { init, track, type BilyClient, type BilyOrder } from '@bilyai/js';
import { trackDataFastCheckoutStarted } from '@/lib/datafast';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

/**
 * Bily tracking for the orsay.ai app.
 *
 * The browser script is loaded exclusively through @bilyai/js — there is no
 * raw <script> tag for Bily anywhere in this app. The URL is public
 * installation data (no private token) and is read unchanged from
 * NEXT_PUBLIC_BILY_SCRIPT_URL, which is frozen into the bundle at build time.
 */
const BILY_SCRIPT_URL = process.env.NEXT_PUBLIC_BILY_SCRIPT_URL;
const BILY_STORE_URL = 'orsay.ai';

/**
 * Self-serve SaaS: users sign up themselves, create an organization, and run
 * an embedded Stripe checkout in-product (src/pages/organizations/create.tsx,
 * src/pages/settings/workspace/billing.tsx). No sales-led motion in this repo.
 */
const BILY_BUSINESS_MODEL = 'saas_self_serve';

/**
 * The exact event names Bily recognises (https://docs.bily.ai/api-reference/events).
 * Names are case-sensitive and are the only thing Bily matches on — a name
 * outside this list is delivered as an anonymous custom event, which is why
 * the Meta-flavoured names this app used first (`CompleteRegistration`,
 * `StartTrial`, `InitiateCheckout`, `Subscribe`) never showed up as funnel
 * steps in the Bily dashboard. The Meta equivalent still travels alongside as
 * the `bily_standard_event` property.
 *
 * `Checkout Started` and `Order Completed` are ecommerce events: the SDK routes
 * them through its ecommerce channel, and only `Order Completed` with an
 * `order` payload carries revenue.
 */
export const BILY_EVENT = {
  signUp: 'Sign Up',
  login: 'Login',
  lead: 'Lead',
  /**
   * The account became usable: a first channel is connected, so conversations
   * can actually reach it. Nothing carried this name before, which is why the
   * dashboard's Activated step sat at 0 while `data_source_connected` (a custom
   * event) went by underneath it.
   */
  activated: 'Activated',
  checkoutStarted: 'Checkout Started',
  trialStarted: 'Trial Started',
  orderCompleted: 'Order Completed',
  subscriptionStarted: 'Subscription Started',
  subscriptionRenewed: 'Subscription Renewed',
  subscriptionUpdated: 'Subscription Updated',
  subscriptionCancelled: 'Subscription Cancelled'
} as const;
export type BilyUser = {
  id: string;
  email?: string;
  /** E.164 when we have it. Sent hashed by Bily; a strong match-quality signal. */
  phone?: string;
  firstName?: string;
  lastName?: string;
};
export type BilyAccountContext = {
  organizationId?: string;
  organizationName?: string;
  plan?: string;
  subscriptionStatus?: string;
  accountCreatedAt?: string;
};

/**
 * Business context that every lifecycle event carries. Only stable IDs and
 * bounded traits — never message bodies, credentials, or payment details.
 */
function accountProperties(account: BilyAccountContext): Record<string, unknown> {
  return {
    organization_id: account.organizationId,
    organization_name: account.organizationName,
    workspace_id: account.organizationId,
    plan: account.plan,
    subscription_status: account.subscriptionStatus,
    account_created_at: account.accountCreatedAt
  };
}

/**
 * The identity block Bily hashes into match keys (em/ph/fn/ln/uid/user_group).
 * Every field we can honestly fill raises the Meta event match quality score,
 * so funnel events should never be emitted with the user id alone when the
 * email and phone are already in hand.
 */
function clientData(user: BilyUser, account: BilyAccountContext = {}): BilyClient {
  return {
    userId: user.id,
    email: user.email,
    phone: user.phone,
    firstname: user.firstName,
    lastname: user.lastName,
    userGroup: account.organizationId
  };
}
export function trackAppEvent(eventName: string, properties: Record<string, unknown> = {}) {
  track(eventName, {
    store_url: BILY_STORE_URL,
    bily_business_model: BILY_BUSINESS_MODEL,
    ...properties
  });
}

/**
 * Emit a lifecycle event tied to the signed-in customer. Call this only from a
 * verified identity point — a Clerk session that is actually established, or a
 * success callback that already resolved.
 */
export function trackIdentifiedEvent(eventName: string, user: BilyUser, account: BilyAccountContext = {}, properties: Record<string, unknown> = {}) {
  trackAppEvent(eventName, {
    client: clientData(user, account),
    user_id: user.id,
    ...accountProperties(account),
    ...properties
  });
}

/**
 * Report money that actually moved. `order` is the only payload Bily turns into
 * revenue: it becomes order_id, value/revenue and currency on the ecommerce
 * channel. Emit it once per Stripe invoice, from the confirmed-payment seam —
 * never from a checkout button.
 */
export function trackOrderCompleted(order: BilyOrder, user: BilyUser, account: BilyAccountContext = {}, properties: Record<string, unknown> = {}) {
  trackAppEvent(BILY_EVENT.orderCompleted, {
    client: clientData(user, account),
    user_id: user.id,
    ...accountProperties(account),
    order,
    ...properties
  });
}

/** Sign-in success. Never call from a button click — only once Clerk confirms the session. */
export function trackSignedInUser(user: BilyUser, account: BilyAccountContext = {}) {
  trackIdentifiedEvent(BILY_EVENT.login, user, account);
}

/**
 * A workspace now exists in both the backend and Clerk, and the two are synced.
 * Emitted from every path that creates one, the inline sign-up flow included,
 * which is where most new accounts are actually born.
 */
export function trackWorkspaceCreated(user: BilyUser, account: BilyAccountContext = {}) {
  trackIdentifiedEvent('workspace_created', user, account, {
    bily_business_event: 'Workspace Created',
    bily_funnel_stage: 'activation',
    bily_optimization_role: 'secondary'
  });
}

/** Where a checkout was started from. Kept as data so one funnel step can hold them all. */
export type CheckoutContext = 'sign_up' | 'organization_create' | 'topUp' | 'promotion' | 'renew';

/**
 * Stripe returned a real checkout session. Call this from the response, never
 * from the button: a click that fails to reach Stripe is not a checkout.
 */
export function trackCheckoutStarted(user: BilyUser, account: BilyAccountContext = {}, ctx: {
  context: CheckoutContext;
  checkoutId?: string;
}) {
  trackIdentifiedEvent(BILY_EVENT.checkoutStarted, user, account, {
    bily_standard_event: 'InitiateCheckout',
    bily_business_event: 'Checkout Started',
    bily_funnel_stage: 'checkout',
    bily_optimization_role: 'secondary',
    checkout_context: ctx.context,
    checkout_id: ctx.checkoutId
  });
  // DataFast rides along on this one seam on purpose. Three pages open a
  // checkout, and two trackers disagreeing about when checkout started would
  // be worse than the coupling. If Bily is ever removed, keep this line and
  // move it, do not delete it with the rest of the file.
  trackDataFastCheckoutStarted(ctx.context, ctx.checkoutId);
}
export function BilyTracking() {
  const $ = _c(6);
  const {
    asPath
  } = useRouter();
  let t0;
  if ($[0] !== asPath) {
    t0 = asPath.split("#");
    $[0] = asPath;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeKey = t0[0];
  const previousUrl = useRef(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(_temp, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const currentUrl = window.location.href;
      if (previousUrl.current === null) {
        const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
        previousUrl.current = navigation?.name ?? currentUrl;
        if (currentUrl === previousUrl.current) {
          return;
        }
      } else {
        if (currentUrl === previousUrl.current) {
          return;
        }
      }
      previousUrl.current = currentUrl;
      track("PageView", {
        sourceUrl: currentUrl,
        pageTitle: document.title
      });
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== routeKey) {
    t3 = [routeKey];
    $[4] = routeKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  return null;
}
function _temp() {
  if (!BILY_SCRIPT_URL) {
    console.error("[Bily] NEXT_PUBLIC_BILY_SCRIPT_URL is not set \u2014 tracking is disabled. It must be present at build time (GitHub Actions variable, CDK build arg, Dockerfile ARG/ENV).");
    return;
  }
  init({
    scriptUrl: BILY_SCRIPT_URL
  });
}